'use client';

import type { ReactNode } from 'react';
import React, { useState, useEffect } from 'react';
import { getAvailableQuantities } from '../../utils';
import { useAddToCTCart } from '@app/pdp/components/AddToCart/hooks/useAddToCTCart';
import { DELIVERY_MESSAGE, DELIVERY_METHODS } from '@app/pdp/constants';
import { useProductDetails } from '@app/pdp/hooks';
import { VetRxCats } from '@app/pdp/types';
import {
    clsx,
    STORE_SERVICES,
    useOptimizely,
    usePageLayout,
} from '@libs/common-next';
import { DeliveryType, getCountry, stringifyStoreId } from '@libs/core';
import { hasStockType, StoreTooltip } from '@libs/shared-ui';
import type { StockStatusType } from '@libs/shared-ui';
import {
    Button,
    SelectField,
    StockStatusEnum,
    StockStatusLabel,
    TextPassage,
} from '@petsmart-ui/sparky';

import Tile from './Tile';
import type { ProductHit } from '@libs/algolia';
import { AddToCartBtn, MinimumQuantityLabel } from '../../../common';
import { EstimatedTreatPoints } from '@app/pdp/components/EstimatedTreatPoints';
import CurbsideDrawer from '../../Bopis/CurbsideDrawer';
import './DeliveryMethodTiles.scss';
import UnavailableDeliveryMethodTile from '../UnavailableDeliveryMethodTile';

type CurbsideInfo = {
    header: string;
    body: ReactNode;
    footer?: ReactNode;
};
// extended BopisProps to add a few new props for optimizely
export type BopisTileProps = {
    product: ProductHit;
    isActive: boolean;
    isDisabled: boolean;
    value: string;
    isDisabledByPersonalization: boolean;
    curbsideInfo: CurbsideInfo;
    stockStatus: StockStatusType;
    vetRxCats?: VetRxCats[];
    minimumCaseQty?: number;
    inventoryQuantity?: number;
    isPageLoading: boolean;
    selection: string;
    setSelection: (value: DeliveryType) => void;
    isTileDisabled?: boolean;
    groupName: string;
};

export default function BopisTile({
    product,
    stockStatus,
    isPageLoading,
    minimumCaseQty,
    inventoryQuantity,
    isDisabled,
    isDisabledByPersonalization,
    curbsideInfo,
    selection,
    setSelection,
    isTileDisabled = false,
    vetRxCats,
    groupName,
}: Readonly<BopisTileProps>) {
    const {
        errorAlert,
        selectedStore: { store },
        fullStoreListResults,
        setStoreDrawerOpen,
    } = usePageLayout();

    const country = getCountry();
    const { addToCTCart, isLoading: isCTCartLoading } = useAddToCTCart();
    const { setCanAddToCart, selectedProduct } = useProductDetails();
    const currentVariantId = Number(selectedProduct.id || product.id);

    const [addedToCart, setAddedToCart] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validStockStatus, setValidStockStatus] =
        useState<StockStatusType>(stockStatus);

    const [showStockInOtherStores, setShowStockInOtherStores] =
        useState<boolean>(false);

    const userContext = useOptimizely().context; // Optimizely

    useEffect(() => {
        if (errorAlert && isLoading) {
            setIsLoading(false);
        }
    }, [errorAlert, isLoading, setIsLoading]);

    // defaulting to true when there is no store. This keeps the UI consistent with previous implementation.
    const bopisEnabled = store ? store.isBopisEnabled : true;
    const isAvailable = store && hasStockType(validStockStatus) && bopisEnabled;

    useEffect(() => {
        setCanAddToCart(Boolean(isAvailable));
    }, [isAvailable, setCanAddToCart]);

    const hasStoresWithStock = fullStoreListResults.find(
        ({ Store }) => Store.HasCurrentProductInStock
    );

    useEffect(() => {
        if (!bopisEnabled) {
            setValidStockStatus(StockStatusEnum.OutOfStock);
        } else {
            setValidStockStatus(stockStatus);
        }
    }, [bopisEnabled, stockStatus]);

    useEffect(() => {
        setShowStockInOtherStores(
            (!isAvailable && hasStoresWithStock !== undefined) || !bopisEnabled
        );
    }, [bopisEnabled, hasStoresWithStock, isAvailable]);

    const [selectedQty, setSelectedQty] = useState(minimumCaseQty || 1);

    const hasCurbsidePickup = store?.StoreServices?.some(
        service => service.ServiceId === STORE_SERVICES.CURBSIDE_PICKUP
    );

    const stockStatusLabel = (
        <div className="bopis-stock-info">
            {stockStatus !== 'Pending' && !showStockInOtherStores ? (
                <div className="stock-status">
                    <StockStatusLabel
                        stockStatus={validStockStatus as StockStatusEnum}
                        labelInStock="In stock "
                        labelFewLeftInStock="Only a few left "
                        labelOutOfStock="Out of stock "
                    />
                    <div className="sparky-c-text-passage sparky-c-stock-status-label">
                        at
                    </div>
                    <StoreTooltip />
                </div>
            ) : null}
        </div>
    );

    const onQuantityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const qty = event?.target.value;

        if (qty) {
            setSelectedQty(parseInt(qty, 10));
        }
    };

    const bopisTextMsg = (): string => {
        if (isAvailable) {
            if (vetRxCats?.includes(VetRxCats.RX)) {
                return `${DELIVERY_MESSAGE.estimated} ${DELIVERY_MESSAGE.vetRxSuffix}`;
            }
            if (vetRxCats?.includes(VetRxCats.VET)) {
                return `${DELIVERY_MESSAGE.ready} ${DELIVERY_MESSAGE.vetRxSuffix}`;
            }
            return DELIVERY_MESSAGE.ready;
        }

        if (!bopisEnabled) {
            return DELIVERY_MESSAGE.nearbyStores;
        }

        return hasStoresWithStock
            ? DELIVERY_MESSAGE.nearbyStores
            : DELIVERY_MESSAGE.ready;
    };

    const availableQuantities = getAvailableQuantities(
        minimumCaseQty,
        inventoryQuantity,
        selectedProduct.maxOrderQty
    );

    if (isDisabledByPersonalization) {
        return (
            <UnavailableDeliveryMethodTile deliveryMethod="Pick up in store" />
        );
    }

    const handleCommercetoolsFormSubmit = async (
        event: React.FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        if (!store || isCTCartLoading || isPageLoading) {
            return;
        }

        const body = {
            country,
            sku: currentVariantId.toString(),
            quantity: selectedQty,
            fulfillmentMethod: DeliveryType.BOPIS,
            storeId: stringifyStoreId(store.StoreNumber),
        };

        await addToCTCart(body);

        userContext?.trackEvent('bopis_atc__ot_'); // Optimizely
    };

    const openStoreDrawer = (e: React.MouseEvent) => {
        e.preventDefault();
        setStoreDrawerOpen({ pageType: 'pdp' });
    };
    const hiddenFormContent = () => (
        <form onSubmit={handleCommercetoolsFormSubmit} data-testid="form-bopis">
            <div className="bopis-body">
                <div className="change-store-section">
                    {store && stockStatusLabel}

                    {(isAvailable || !hasStoresWithStock) && (
                        <Button
                            text={
                                store && store.StoreNumber
                                    ? 'Change store'
                                    : 'Find a store near me'
                            }
                            variant="link"
                            className="change-store-btn"
                            onClick={openStoreDrawer}
                        />
                    )}
                </div>
            </div>
            {isAvailable && store && (
                <>
                    {hasCurbsidePickup && !showStockInOtherStores && (
                        <CurbsideDrawer curbsideInfo={curbsideInfo} />
                    )}
                    <MinimumQuantityLabel minimumCaseQty={minimumCaseQty} />
                    <div className="form-selected-content">
                        <SelectField
                            className="qty-selector"
                            value={selectedQty}
                            onChange={onQuantityChange}
                            label="Qty"
                            items={availableQuantities}
                            disabled={availableQuantities.length < 1}
                        />
                        <AddToCartBtn
                            text={
                                addedToCart
                                    ? DELIVERY_METHODS.ADDED_TO_CART
                                    : DELIVERY_METHODS.ADD_TO_CART
                            }
                            className={clsx(
                                addedToCart &&
                                    'add-to-cart-button add-to-cart-button__green'
                            )}
                            setAddedToCart={setAddedToCart}
                            addedToCart={addedToCart}
                            isLoading={
                                isLoading || isCTCartLoading || isPageLoading
                            }
                        />
                    </div>
                    <EstimatedTreatPoints
                        qty={selectedQty}
                        shopId={store.StoreNumber}
                    />
                </>
            )}
            {showStockInOtherStores && (
                <Button
                    loading={isPageLoading}
                    className="alternative-btn"
                    text="Show in-stock stores"
                    variant="primary"
                    onClick={openStoreDrawer}
                />
            )}

            {!isAvailable && !hasStoresWithStock && bopisEnabled && (
                <AddToCartBtn disabled />
            )}
        </form>
    );
    // Added new optimizely tile for unavailable and replace return component for new layout
    if (isDisabledByPersonalization) {
        return (
            <UnavailableDeliveryMethodTile deliveryMethod="Pick up in store" />
        );
    }

    return (
        <>
            <Tile
                data-testid={`${DeliveryType.BOPIS}__tile`}
                value={DeliveryType.BOPIS}
                selectedValue={selection}
                groupName={groupName}
                onChange={setSelection}
                isTileDisabled={isTileDisabled || isDisabled}
                iconName="store-outline"
                title={
                    <span className="radio-field-title sparky-u-theme-typography-body-lg-bold">
                        Pick up in store
                    </span>
                }
            >
                {isTileDisabled || (isDisabled && bopisEnabled) ? (
                    <TextPassage className="delivery-method-status sparky-u-color-utility-warning">
                        Not available for this item
                    </TextPassage>
                ) : (
                    <TextPassage
                        className={`${
                            isAvailable && !vetRxCats?.includes(VetRxCats.RX)
                                ? 'available'
                                : ''
                        }`}
                        size="sm"
                    >
                        {bopisTextMsg()}
                    </TextPassage>
                )}
            </Tile>
            {selection == DeliveryType.BOPIS && !isDisabled ? (
                <div id="bopis-form" className="bopis">
                    {hiddenFormContent()}
                </div>
            ) : (
                <></>
            )}
        </>
    );
}
